import { AttributionEffectEventType as AttributionOutcomeType } from "@redotech/crdb-enums/attribution/attribution-effect-event-type";
import {
  AttributionEvidenceType,
  type AttributionSettings,
} from "@redotech/redo-attribution-common/attribution-settings";
import { TimeUnit } from "@redotech/redo-common-model/time";

/** The evidence the page has a checkbox for, one each. The rest always keeps
 *  its saved config. */
export const FILTERABLE_EVIDENCE: readonly AttributionEvidenceType[] = [
  AttributionEvidenceType.EMAIL_CLICK,
  AttributionEvidenceType.EMAIL_OPEN,
  AttributionEvidenceType.EMAIL_DELIVERED,
  AttributionEvidenceType.SMS_CLICK,
  AttributionEvidenceType.SMS_SEND,
];

/** The attribution window the marketing analytics surfaces fall back to. */
export const DEFAULT_ATTRIBUTION_DAYS = 5;

/** The day counts the dropdown offers. */
export const SELECTABLE_ATTRIBUTION_WINDOWS = [
  1, 2, 3, 4, 5, 7, 10, 14, 21, 30,
] as const;

export interface AnalyticsAttributionFilters {
  /** Undefined keeps each evidence type's saved window. */
  attributionDays: number | undefined;
  attributionEvidence: AttributionEvidenceType[];
}

/** The team's saved settings with the analytics page's filters applied. Each
 *  undefined filter keeps what the settings say. */
export function analyticsAttributionSettings({
  settings,
  attributionDays,
  attributionEvidence,
}: {
  settings: AttributionSettings;
  attributionDays: number | undefined;
  attributionEvidence: readonly AttributionEvidenceType[] | undefined;
}): AttributionSettings {
  const selected = attributionEvidence && new Set(attributionEvidence);
  const order = settings[AttributionOutcomeType.Order];
  const evidence = { ...order.evidence };
  for (const evidenceType of FILTERABLE_EVIDENCE) {
    const config = evidence[evidenceType];
    if (config) {
      evidence[evidenceType] = {
        enabled: selected ? selected.has(evidenceType) : config.enabled,
        window:
          attributionDays === undefined
            ? config.window
            : { value: attributionDays, unit: TimeUnit.DAY },
      };
    }
  }
  return {
    ...settings,
    [AttributionOutcomeType.Order]: { ...order, evidence },
  };
}

/** What the page opens with: the saved settings, unchanged. */
export function analyticsFiltersFromSettings(
  settings: AttributionSettings,
): AnalyticsAttributionFilters {
  const { evidence } = settings[AttributionOutcomeType.Order];
  return {
    attributionDays: undefined,
    attributionEvidence: FILTERABLE_EVIDENCE.filter(
      (evidenceType) => evidence[evidenceType]?.enabled,
    ),
  };
}
